/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import type React from 'react';

import { type PolymorphicCommonProps } from '../_common/types';
import { IconButtonBase, type IconButtonBaseProps } from '../icon-button-base';

type CleanIconButtonButtonProps = Omit<
  IconButtonBaseProps,
  'iconButtonVariant' | 'isFloating'
>;

export const CleanIconButton = <T extends React.ElementType = 'button'>({
  children,
  as,
  isLoading = false,
  isDisabled = false,
  size = 'medium',
  // Does not have a default value to avoid setting aria-pressed to true if not explicitly set
  isActive,
  variant = 'neutral',
  description,
  tooltipProps,
  className,
  style,
  htmlAttributes,
  onClick,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, CleanIconButtonButtonProps>) => {
  return (
    <IconButtonBase
      as={as}
      iconButtonVariant="clean"
      isDisabled={isDisabled}
      size={size}
      isLoading={isLoading}
      isActive={isActive}
      variant={variant}
      description={description}
      tooltipProps={tooltipProps}
      className={className}
      style={style}
      htmlAttributes={htmlAttributes}
      onClick={onClick}
      ref={ref}
      {...restProps}
    >
      {children}
    </IconButtonBase>
  );
};
